/**
 * Navigation utilities for handling external links and social media sharing
 */

/**
 * Opens a URL in a new window with security features
 *
 * @param baseUrl - The base URL to open
 * @param searchParams - Optional search parameters to append to the URL
 * @param windowFeatures - Optional window features (default: 'noopener,noreferrer')
 */
export function openInNewWindow(
  baseUrl: string,
  searchParams?: Record<string, string>,
  windowFeatures: string = 'noopener,noreferrer',
): void {
  const url = new URL(baseUrl);

  if (searchParams) {
    Object.entries(searchParams).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });
  }

  window.open(url.toString(), '_blank', windowFeatures);
}

/**
 * Gets the correct base path for dashboard URLs based on the environment.
 *
 * In development mode (NODE_ENV === 'development'), uses the actual app path '/event-dashboard-webapp'.
 * In other environments (production, test, etc.), uses the user-facing path '/events' which CloudFront
 * rewrites to '/event-dashboard-webapp' transparently.
 *
 * @returns The appropriate base path for the current environment
 */
export function getBasePath(): string {
  return process.env.NODE_ENV === 'development' ? '/event-dashboard-webapp' : '/events';
}

/**
 * Constructs a dashboard URL for a specific event.
 * Automatically uses the correct base path for the current environment.
 *
 * @param eventId - The ID of the event
 * @returns The full dashboard URL path
 *
 * @example
 * // Development: '/event-dashboard-webapp/123/dashboard/'
 * // Production/Test: '/events/123/dashboard/'
 * getDashboardUrl('123')
 */
export function getDashboardUrl(eventId: string): string {
  return `${getBasePath()}/${eventId}/dashboard/`;
}
